import { Utensils } from 'lucide-react';
import { ReactNode } from 'react';
import { MealArt } from './meal-art';
import { MealClock } from './meal-clock';

export interface MealHeroProps {
  eyebrow?: string;
  title: string;
  description: ReactNode;
  /** When true, banner turns into soft success green */
  isDone?: boolean;
  /** When true, displays the integrated countdown timer */
  showClock?: boolean;
  targetDate?: string;
  className?: string;
}

/**
 * Meal Hero banner with warm tones, decorative background food art,
 * eyebrow badge, large bold typography, and an integrated countdown clock.
 * Matches HR Module Prototype `.meal-hero.has-clock`.
 */
export const MealHero = ({
  eyebrow = 'Lunch',
  title,
  description,
  isDone = false,
  showClock = false,
  targetDate,
  className = '',
}: MealHeroProps) => {
  return (
    <section
      aria-labelledby="meal-hero-heading"
      className={`relative overflow-hidden rounded-lg border transition-all duration-300 ${
        isDone
          ? 'border-meal-done-border bg-meal-done text-foreground'
          : 'border-meal-hero-border bg-meal-hero text-foreground'
      } ${className}`}
    >
      {/* Decorative food artwork */}
      <MealArt
        className={
          isDone
            ? 'text-meal-done-art'
            : 'text-meal-art'
        }
      />

      {/* Grid container: main text | vertical divider | clock */}
      <div
        className={`relative z-10 grid gap-0 ${
          showClock && targetDate
            ? 'grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_280px]'
            : 'grid-cols-1'
        }`}
      >
        {/* Banner main content */}
        <div className="flex flex-col justify-end p-5 sm:p-6 md:p-7">
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase sm:text-sm ${
              isDone
                ? 'text-tint-green-foreground'
                : 'text-tint-gold-foreground'
            }`}
          >
            <Utensils className="size-3.5 sm:size-4 shrink-0 stroke-[2]" />
            {eyebrow}
          </span>

          <h2
            id="meal-hero-heading"
            className="mt-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
          >
            {title}
          </h2>

          <div className="mt-1.5 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            {description}
          </div>
        </div>

        {/* Vertical divider (desktop) / horizontal divider (mobile) */}
        {showClock && targetDate && (
          <>
            <span
              aria-hidden="true"
              className={`my-0 h-px w-full md:my-5 md:h-auto md:w-px ${
                isDone
                  ? 'bg-meal-done-border'
                  : 'bg-meal-hero-border'
              }`}
            />

            {/* Countdown Clock */}
            <div className="p-5 pt-0 md:p-6 md:pl-0">
              <MealClock targetDate={targetDate} />
            </div>
          </>
        )}
      </div>
    </section>
  );
};
